import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Lock, ChevronDown, Award } from "lucide-react";
import { useCourse, useProgress, currentTierData, TIER_COPY } from "@/lib/hooks";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { ExecutiveLearn } from "@/components/executive/ExecutiveHome";
import { GlossaryList } from "@/pages/Glossary";
import { t, colon, tierLevel } from "@/lib/i18n";
import { MachineTranslationBanner } from "@/components/LanguagePicker";

export default function Learn() {
  const { user } = useAuth();
  return user.track === "executive" ? <ExecutiveLearn /> : <StrategistLearn />;
}

const lessonHref = (l) => `/lessons/${l.id}`;

const ModuleRow = ({ m, s, done, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const pct = Math.round((s.lessons_done / s.lessons_total) * 100);
  const next = m.lessons.find((l) => !done.has(l.id)) || m.lessons[0];
  return (
    <div data-testid={`learn-module-${m.id}`} className={`rounded-xl border bg-card transition-colors ${open ? "border-brand-primary" : "border-border hover:border-brand-primary/60"} ${!s.open ? "opacity-50" : ""}`}>
      <button type="button" disabled={!s.open} onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`learn-module-toggle-${m.id}`} className="flex w-full items-center gap-4 p-4 text-left sm:p-5">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-heading font-bold ${s.passed ? "bg-brand-accent/15 text-brand-accent" : "bg-muted"}`}>{s.passed ? <Check className="h-5 w-5" /> : !s.open ? <Lock className="h-4 w-4" /> : m.number}</span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-snug">{m.title}</div>
          {m.badge && <div data-testid={`learn-badge-${m.id}`} className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${s.passed ? "text-brand-accent" : "text-muted-foreground"}`}><Award className="h-3.5 w-3.5" />{s.passed ? `${t("Badge earned")}${colon()} ${m.badge}` : `${t("Badge")}${colon()} ${m.badge}`}</div>}
          <div className="mt-2 flex items-center gap-3"><div className="h-1.5 flex-1 rounded-full bg-muted"><div className="h-full rounded-full bg-brand-primary" style={{ width: `${pct}%` }} /></div><span className="text-xs text-muted-foreground">{s.lessons_done}/{s.lessons_total}</span></div>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180 text-brand-primary" : ""}`} />
      </button>
      {open && s.open && (
        <div className="fade-up border-t border-border px-4 pb-4 sm:px-5">
          <ol className="mt-2">
            {m.lessons.map((l) => (
              <li key={l.id}>
                <Link to={lessonHref(l)} data-testid={`learn-lesson-${l.id}`} className="group flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-muted">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs ${done.has(l.id) ? "border-brand-accent bg-brand-accent text-brand-accent-fg" : "border-border"}`}>{done.has(l.id) ? <Check className="h-3.5 w-3.5" /> : l.kind === "lab" ? "L" : ""}</span>
                  <span className="flex-1 text-sm font-medium">{l.title}</span>
                  <span className="text-xs text-muted-foreground">{l.minutes} min</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-brand-primary" />
                </Link>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Link to={lessonHref(next)} data-testid={`learn-continue-${m.id}`} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-brand-primary-fg hover:opacity-90">{s.lessons_done ? t("Continue") : t("Start")}{colon()} {next.title} <ArrowRight className="h-4 w-4" /></Link>
            <Link to={`/modules/${m.id}`} data-testid={`learn-module-page-${m.id}`} className="text-sm font-semibold text-muted-foreground hover:text-brand-primary">{t("Module quizzes →")}</Link>
          </div>
        </div>
      )}
    </div>
  );
};

function StrategistLearn() {
  const { data: course } = useCourse();
  const { data: progress } = useProgress();
  if (!course || !progress) return <Loader />;
  const td = currentTierData(progress);
  const done = new Set(td.lessons_done);
  const firstOpen = td.modules.find((x) => x.open && !x.passed)?.module_id;
  return (
    <PageShell>
      <MachineTranslationBanner />
      <div className="overline-label">{tierLevel(TIER_COPY[td.tier].label)}</div>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">{t("Learn")}</h1>
      <p className="mt-2 text-muted-foreground">{t("learn-sub") === "learn-sub" ? "8 modules over 3 days. Finish the lessons, then pass the module check with 8 of 10." : t("learn-sub")}</p>
      {[1, 2, 3].map((d) => (
        <section key={d} className="mt-10">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">{t("Day")} {d} · {t(["See", "Design", "Deploy"][d - 1])}</h2>
          <div className="space-y-3">{course.modules.filter((m) => m.day === d).map((m) => <ModuleRow key={m.id} m={m} s={td.modules.find((x) => x.module_id === m.id)} done={done} defaultOpen={m.id === firstOpen} />)}</div>
        </section>
      ))}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-bold">{t("Glossary")}</h2>
        <p className="mb-6 mt-1 text-muted-foreground">{t("glossary-sub") === "glossary-sub" ? "Simple meanings for the words in the lessons." : t("glossary-sub")}</p>
        <GlossaryList />
      </section>
    </PageShell>
  );
}
